import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { saveAs } from "file-saver";
import * as XLSXStyle from "xlsx-js-style";
import {
  texto,
  numero,
  moneda,
  construirHojaTabla,
  construirLibroBlob,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import { celdaTexto } from "../../../../Components/ExcelEstilos";
import inyectarLogoEnExcel from "../../../../Components/ExcelLogo";
import cuentaMask from "../../../../Components/cuentaMask";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/*
  Excel y PDF del Estado de Resultados por Periodo.

  La matriz se arma UNA vez como {columnas, filas, totales} y de ahi salen los
  dos archivos, igual que en `ReporteCostoVsUltimaCompra/exportarCostoVsUltimaCompra.js`.
  El backend ya manda los renglones en orden de presentacion (cuentas, subtotales
  y utilidades intercaladas), asi que aqui no se reordena nada: si se reconstruyera
  el orden del P&L en cada renderer, tarde o temprano dirian cosas distintas.
*/

// Arriba de esto la matriz no cabe en LETTER horizontal ni quitando columnas.
export const MAX_PERIODOS_PDF = 8;

const TITULO = "Estado de Resultados por Periodo";

const NOMBRES_PERIODICIDAD = {
  mensual: "Mensual",
  semanal: "Semanal",
  quincenal: "Quincenal",
  trimestral: "Trimestral",
  anual: "Anual",
};

const nombreArchivo = (empresaNombre) =>
  `Estado_De_Resultados_Por_Periodo_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const resalteDe = (renglon) => {
  if (renglon.tipo === "utilidad") return "utilidad";
  if (renglon.tipo === "subtotal") return "subtotal";
  return null;
};

// La sangria por nivel es lo unico que da a entender la jerarquia en una tabla
// plana; en el Excel se hace con espacios porque una celda no tiene indentacion.
const conceptoDe = (renglon) =>
  renglon.tipo === "cuenta"
    ? `${"    ".repeat(renglon.sangria || 0)}${renglon.descripcion || ""}`
    : renglon.descripcion || "";

/**
 * Rojo de los importes en negativo. Va con "#" porque lo consumen pdfmake y el
 * CSS de la tabla; `ExcelTablaReporte` le quita el "#" solo.
 * Es el rojo de la paleta de la app (antd red-7), legible sobre blanco y sobre
 * los fondos de subtotal/utilidad.
 */
export const ROJO_NEGATIVO = "#CF1322";

export const esNegativo = (valor) => Number(valor) < 0;

const celda = (constructor, valor, resalte) => {
  const base = valor === null || valor === undefined ? texto("-") : constructor(valor);
  const conResalte = resalte ? { ...base, resalte } : base;
  // El color de la letra se aplica encima del resalte, asi que un subtotal
  // negativo conserva su fondo y ademas sale en rojo.
  return esNegativo(valor) ? { ...conResalte, colorTexto: ROJO_NEGATIVO } : conResalte;
};

export const construirTabla = ({ periodos, renglones, parametros }) => {
  const comparativo = !!(parametros && parametros.comparativo);
  const porcentajes = !!(parametros && parametros.porcentajes);

  const columnas = [
    { titulo: "Cuenta", tipo: "texto", ancho: 18, anchoPdf: 78 },
    { titulo: "Concepto", tipo: "texto", ancho: 46, anchoPdf: 170 },
  ];

  (periodos || []).forEach((periodo) => {
    columnas.push({ titulo: periodo.label, tipo: "moneda", ancho: 16, anchoPdf: 70 });
    if (comparativo) {
      // Fuera del PDF: con comparativo se triplican las columnas y ya no cabe.
      columnas.push({ titulo: `${periodo.label} A.A.`, tipo: "moneda", ancho: 16, enPdf: false });
      columnas.push({ titulo: `${periodo.label} Var %`, tipo: "numero", ancho: 12, enPdf: false });
    }
    if (porcentajes) {
      columnas.push({ titulo: `${periodo.label} %`, tipo: "numero", ancho: 10, enPdf: false });
    }
  });

  columnas.push({ titulo: "Total", tipo: "moneda", ancho: 18, anchoPdf: 78 });
  if (comparativo) {
    columnas.push({ titulo: "Total A.A.", tipo: "moneda", ancho: 18, enPdf: false });
    columnas.push({ titulo: "Total Var %", tipo: "numero", ancho: 12, enPdf: false });
  }
  if (porcentajes) {
    columnas.push({ titulo: "Total %", tipo: "numero", ancho: 10, enPdf: false });
  }

  const filas = (renglones || []).map((renglon) => {
    const resalte = resalteDe(renglon);
    const fila = [
      celda(texto, renglon.tipo === "cuenta" ? cuentaMask(renglon.codigo || "") : "", resalte),
      celda(texto, conceptoDe(renglon), resalte),
    ];

    (periodos || []).forEach((periodo) => {
      fila.push(celda(moneda, (renglon.importes || {})[periodo.key] || 0, resalte));
      if (comparativo) {
        fila.push(celda(moneda, (renglon.importesAnterior || {})[periodo.key] || 0, resalte));
        fila.push(celda(numero, (renglon.variacionPct || {})[periodo.key], resalte));
      }
      if (porcentajes) {
        fila.push(celda(numero, (renglon.porcentaje || {})[periodo.key], resalte));
      }
    });

    fila.push(celda(moneda, renglon.total || 0, resalte));
    if (comparativo) {
      fila.push(celda(moneda, renglon.totalAnterior || 0, resalte));
      fila.push(celda(numero, renglon.variacionPctTotal, resalte));
    }
    if (porcentajes) {
      fila.push(celda(numero, renglon.porcentajeTotal, resalte));
    }

    return fila;
  });

  // Sin `totalizar`: una fila TOTAL sumaria ingresos con gastos y ademas con los
  // subtotales, que ya estan en la tabla. Con los totales en null,
  // `construirHojaTabla` omite la fila.
  return { columnas, filas, totales: columnas.map(() => null) };
};

/** Pie de filtros del Excel y del PDF. Las cuentas omitidas van SIEMPRE: un
 *  estado de resultados que no dice que se dejo fuera no es auditable. */
export const describirFiltros = ({ parametros, nombreProyectos }) => {
  if (!parametros) return "";
  const partes = [
    `Del ${moment(parametros.fechaInicial).format("DD/MM/YYYY")} al ${moment(parametros.fechaFinal).format("DD/MM/YYYY")}`,
    `Periodicidad: ${NOMBRES_PERIODICIDAD[parametros.periodicidad] || parametros.periodicidad}`,
    `Nivel: ${parametros.nivel}`,
  ];
  if (nombreProyectos) partes.push(nombreProyectos);
  if (parametros.comparativo) partes.push("Comparativo vs ano anterior");

  const omitidas = parametros.cuentasExcluidas || [];
  partes.push(
    omitidas.length === 0
      ? "Sin cuentas omitidas"
      : `Cuentas omitidas: ${omitidas
          .map((cuenta) => `${cuentaMask(cuenta.codigo || "")} ${cuenta.descripcion || ""}`.trim())
          .join("; ")}`
  );

  return partes.join(" | ");
};

// La grafica va en su propia hoja para no tapar celdas de la tabla. La hoja solo
// lleva el titulo; la imagen flota anclada encima.
const hojaDeGrafica = (descripcionFiltros) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    [celdaTexto(TITULO, { font: { bold: true, sz: 13 } })],
    [celdaTexto(descripcionFiltros || "", { font: { sz: 9, color: { rgb: "8C8C8C" } } })],
  ]);
  hoja["!cols"] = new Array(14).fill({ wch: 12 });
  return hoja;
};

/**
 * @param {Object} [grafica] `{dataUrl, ancho, alto}` de `rasterizarGrafica`. Si no
 *   viene, el Excel sale sin grafica — es un respaldo valido, la tabla es lo que
 *   importa.
 * @returns {Promise<{conGrafica: boolean}>} para que la UI pueda avisar si la
 *   grafica no se pudo incrustar.
 */
export const descargarExcel = async ({ datos, empresaNombre, descripcionFiltros, grafica }) => {
  const hojas = [
    {
      nombre: "Estado de Resultados",
      hoja: construirHojaTabla(construirTabla(datos), {
        titulo: TITULO,
        empresaNombre,
        descripcionFiltros,
        sinAutofiltro: true,
      }),
    },
  ];

  if (!grafica) {
    descargarLibro(hojas, nombreArchivo(empresaNombre));
    return { conGrafica: false };
  }

  hojas.push({ nombre: "Grafica", hoja: hojaDeGrafica(descripcionFiltros) });
  const libro = construirLibroBlob(hojas);

  // `xlsx-js-style` no escribe graficas nativas de Excel, asi que se inyecta el
  // PNG en el zip. Si falla, se descarga el libro sin imagen igual.
  const conImagen = await inyectarLogoEnExcel(
    libro,
    {
      base64: grafica.dataUrl.split(",")[1],
      base64Completa: grafica.dataUrl,
      extension: "png",
    },
    {
      hoja: 2,
      fila: 3,
      columna: 0,
      nombre: "Grafica",
      anchoMax: grafica.ancho,
      altoMax: grafica.alto,
    }
  );

  saveAs(conImagen || libro, `${nombreArchivo(empresaNombre)}.xlsx`);
  return { conGrafica: !!conImagen };
};

export const descargarPdf = ({ datos, empresaNombre, descripcionFiltros, grafica }) => {
  const tabla = construirTabla(datos);
  const utilidadNeta = (datos.renglones || []).find((renglon) => renglon.clave === "neta");
  const totalIngresos = (datos.renglones || []).find(
    (renglon) => renglon.tipo === "subtotal" && renglon.clave === "ingresos"
  );

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: `Reporte ${TITULO}`,
        empresaNombre,
        descripcionFiltros,
        indicadores: [
          { etiqueta: "Periodos", valor: String((datos.periodos || []).length) },
          { etiqueta: "Ingresos", valor: String(totalIngresos ? totalIngresos.total : 0) },
          { etiqueta: "Utilidad neta", valor: String(utilidadNeta ? utilidadNeta.total : 0) },
        ],
        contador: tabla.filas.length,
        imagen: grafica ? grafica.dataUrl : undefined,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
